import { useEffect, useState } from "react";
import { Link } from "react-router";
import { WEEKDAY_NAMES } from "@krut/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useSettings } from "../hooks.ts";
import { Button, Card, cx, ErrorText, Input, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";
import { useSaveSettings } from "./Settings.tsx";

/** Schedules & briefings → Briefings: the person's daily briefing and weekly retrospective. */
export function MyBriefingsPage() {
  const settings = useSettings();
  const save = useSaveSettings();
  if (settings.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (settings.error) return <ErrorText error={settings.error} />;
  const b = settings.data.briefing;
  const none = !b.available && !b.group && !b.weeklyAvailable;
  return (
    <>
      <PageHeader title="Briefings" description="Your assistant reaches out on its own: a short plan for the day, and once a week a look back at what got done." />
      {none ? (
        <Card className="p-6 text-sm text-[var(--muted)]">Briefings are switched off for your group at the moment. You can still ask your assistant for a briefing in chat any time.</Card>
      ) : (
        <>
          <BriefingSection briefing={b} save={save} />
          <WeeklySection briefing={b} save={save} />
          <ErrorText error={save.error} />
        </>
      )}
    </>
  );
}

type Briefing = NonNullable<ReturnType<typeof useSettings>["data"]>["briefing"];

const channelText = (c: Briefing["channel"]) =>
  c === "web" ? (
    <>in the web app, as a chat. <Link to="/connections/chat" className="text-[var(--color-accent)] hover:underline">Link Slack, Discord or WhatsApp</Link> to get it there instead.</>
  ) : c === "email" ? (
    <>by email, with a link to the chat. <Link to="/connections/chat" className="text-[var(--color-accent)] hover:underline">Link Slack, Discord or WhatsApp</Link> to get it there instead.</>
  ) : (
    <>as a direct message on {c[0]!.toUpperCase() + c.slice(1)}.</>
  );

/** The person's daily briefing: when it comes, where, and the option to move or mute it. */
function BriefingSection({ briefing, save }: { briefing: Briefing; save: ReturnType<typeof useSaveSettings> }) {
  const qc = useQueryClient();
  const own = briefing.times !== null;
  const [times, setTimes] = useState<string[]>(briefing.times ?? briefing.group?.times ?? ["10:00"]);
  useEffect(() => setTimes(briefing.times ?? briefing.group?.times ?? ["10:00"]), [briefing.times, briefing.group?.times]);
  const now = useMutation({
    mutationFn: () => unwrap(api.me.settings.briefing.now.$post({ json: { kind: "briefing" } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dash-chats"] }),
  });
  if (!briefing.available && !briefing.group) return null;
  const groupLabel = briefing.group ? `${briefing.group.times.join(" and ")}${briefing.group.weekdaysOnly ? " on weekdays" : " every day"}` : "";
  return (
    <>
      <h2 className="mb-3 text-base font-semibold">Daily briefing</h2>
      <Card className="mb-8 space-y-4 p-5">
        <p className="text-sm text-[var(--muted)]">
          {!briefing.available ? (
            "Briefings are switched off for your group at the moment."
          ) : briefing.muted ? (
            "Your briefing is muted."
          ) : (
            <>
              Your assistant sends you a short plan for the day {briefing.next ? <>— next one {briefing.next.today ? "today" : "on the next working day"} at {briefing.next.time}</> : null}. It reaches you {channelText(briefing.channel)}
            </>
          )}
        </p>
        {briefing.available && (
          <>
            <Switch label="Mute my briefing" hint="Your assistant stops reaching out on its own; you can still ask it for a briefing any time." checked={briefing.muted} disabled={save.isPending} onChange={(v) => save.mutate({ briefing: { muted: v } })} />
            {!briefing.muted && (
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  {(
                    [
                      [false, `My group's schedule (${groupLabel})`],
                      [true, "My own times"],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => (v ? setTimes(briefing.times ?? briefing.group?.times ?? ["10:00"]) : save.mutate({ briefing: { times: null } }))}
                      className={cx("rounded-lg border px-3 py-1.5 text-sm transition", own === v ? "border-[var(--color-accent)]/50 bg-[var(--color-brand)]/10 text-[var(--text)] font-medium" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {(own || times !== (briefing.times ?? briefing.group?.times)) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {times.map((t, i) => (
                      <span key={i} className="flex items-center gap-1">
                        <Input type="time" value={t} onChange={(e) => setTimes(times.map((x, j) => (j === i ? e.target.value : x)))} className="w-32" />
                        {times.length > 1 && (
                          <button type="button" className="text-xs text-[var(--muted)] hover:text-red-700" onClick={() => setTimes(times.filter((_, j) => j !== i))}>
                            remove
                          </button>
                        )}
                      </span>
                    ))}
                    {times.length < 3 && (
                      <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setTimes([...times, "16:00"])}>
                        + another time
                      </button>
                    )}
                    <Button variant="primary" disabled={save.isPending || times.some((t) => !/^\d\d:\d\d$/.test(t)) || JSON.stringify(times) === JSON.stringify(briefing.times)} onClick={() => save.mutate({ briefing: { times } })}>
                      Save times
                    </Button>
                  </div>
                )}
                {briefing.group?.weekdaysOnly && <p className="mt-2 text-xs text-[var(--muted)]">Weekdays only, as set for your group.</p>}
              </div>
            )}
          </>
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--line)] pt-4 text-sm">
          <Button disabled={now.isPending} onClick={() => now.mutate()}>{now.isPending ? "Your assistant is writing it…" : "Send me a briefing now"}</Button>
          {now.data && (
            <span className="text-[var(--muted)]">
              Sent {now.data.deliveredVia === "web" ? "— open it in " : now.data.deliveredVia === "email" ? "by email; also in " : `on ${now.data.deliveredVia}; also in `}
              <Link to={`/chat/${now.data.sessionId ?? ""}`} className="text-[var(--color-accent)] hover:underline">Chat</Link>.
            </span>
          )}
          <ErrorText error={now.error} />
        </div>
      </Card>
    </>
  );
}

/** The weekly retrospective: a look back at the week and a conversation about goals, at the group's day and time or the person's own. */
function WeeklySection({ briefing, save }: { briefing: Briefing; save: ReturnType<typeof useSaveSettings> }) {
  const qc = useQueryClient();
  const group = briefing.group;
  const own = briefing.weeklyDay !== null || briefing.weeklyTime !== null;
  const [day, setDay] = useState<number>(briefing.weeklyDay ?? group?.weeklyDay ?? 5);
  const [time, setTime] = useState<string>(briefing.weeklyTime ?? group?.weeklyTime ?? "16:00");
  const [editing, setEditing] = useState(false);
  const now = useMutation({
    mutationFn: () => unwrap(api.me.settings.briefing.now.$post({ json: { kind: "weekly" } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dash-chats"] }),
  });
  if (!briefing.weeklyAvailable) return null;
  const groupLabel = group ? `${WEEKDAY_NAMES[group.weeklyDay]}s at ${group.weeklyTime}` : "";
  const next = briefing.weeklyNext;
  return (
    <>
      <h2 className="mb-3 text-base font-semibold">Weekly retrospective</h2>
      <Card className="mb-8 space-y-4 p-5">
        <p className="text-sm text-[var(--muted)]">
          {briefing.weeklyMuted ? (
            "Your weekly retrospective is muted."
          ) : (
            <>
              Once a week your assistant looks back at what got done, how your <Link to="/goals" className="text-[var(--color-accent)] hover:underline">goals</Link> are going, and what matters next week — then you can talk it through: change a goal, move a deadline, or add a new one.
              {next && <> Next one {next.inDays === 0 ? "today" : next.inDays === 1 ? "tomorrow" : `on ${WEEKDAY_NAMES[next.day]}`} at {next.time}.</>}
            </>
          )}
        </p>
        <Switch label="Mute my weekly retrospective" checked={briefing.weeklyMuted} disabled={save.isPending} onChange={(v) => save.mutate({ briefing: { weeklyMuted: v } })} />
        {!briefing.weeklyMuted && (
          <div>
            <div className="mb-2 flex flex-wrap gap-2">
              {(
                [
                  [false, `My group's time (${groupLabel})`],
                  [true, "My own day and time"],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => (v ? setEditing(true) : (setEditing(false), save.mutate({ briefing: { weeklyDay: null, weeklyTime: null } })))}
                  className={cx("rounded-lg border px-3 py-1.5 text-sm transition", (own || editing) === v ? "border-[var(--color-accent)]/50 bg-[var(--color-brand)]/10 text-[var(--text)] font-medium" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
                >
                  {label}
                </button>
              ))}
            </div>
            {(own || editing) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-40">
                  <Select value={day} onChange={(e) => setDay(Number(e.target.value))}>
                    {WEEKDAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </Select>
                </span>
                <span className="w-32">
                  <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                </span>
                <Button
                  variant="primary"
                  disabled={save.isPending || !/^\d\d:\d\d$/.test(time) || (day === briefing.weeklyDay && time === briefing.weeklyTime)}
                  onClick={() => save.mutate({ briefing: { weeklyDay: day, weeklyTime: time } }, { onSuccess: () => setEditing(false) })}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--line)] pt-4 text-sm">
          <Button disabled={now.isPending} onClick={() => now.mutate()}>{now.isPending ? "Your assistant is writing it…" : "Send me this week's retrospective now"}</Button>
          {now.data && (
            <span className="text-[var(--muted)]">
              Sent — <Link to={`/chat/${now.data.sessionId ?? ""}`} className="text-[var(--color-accent)] hover:underline">open it in Chat</Link>.
            </span>
          )}
          <ErrorText error={now.error} />
        </div>
      </Card>
    </>
  );
}
